import { AxiosError, AxiosResponse } from 'axios'
import { HouseCardItemProps } from '../reducer/homeReducer'
import { MainFilters } from '../reducer/searchReducer'
import { IBasicFilters } from '../types'
import { MainAction } from './models'
import * as SentryClient from '@sentry/nextjs'
import {
  FETCH_FIRST_BOOKING_ITEMS,
  FETCH_FIRST_SEARCH_ITEMS,
  FETCH_NEIGHBOURHOODS,
  FETCH_PLACE_ITEMS,
  FETCH_SEARCH_ITEMS,
  SET_BASIC_FILTERS,
  SET_FILTERS,
} from './types'
import heap from '@/helpers/heap'

type IHouseRequestType = {
  perpage?: number
  page?: number
  instant?: boolean
  boomgardi?: boolean
  from_map_nearby?: number
  phrase?: string
  near_cities?: string[]
  mid_term?: number
}

export const fetchHouses: MainAction<any> =
  (queries: IHouseRequestType) =>
  async (dispatch, getState, { Api, ctx }) => {
    const api = new Api({
      dispatch,
      getState,
      ctx,
    })
    const {
      search: { list },
    } = getState()

    try {
      dispatch({
        type: FETCH_SEARCH_ITEMS,
        ...getState().search,
        loading: true,
        list: [],
        map_list: [],
        midterm_map_list: getState().search.midterm_map_list,
        search_id: '',
        ...(!getState().search?.min_price?.amount && {
          min_price: {
            amount: 30000,
            currency_name: 'تومان',
            currency_short_form: 'ت',
          },
        }),
      })

      // eslint-disable-next-line no-unused-vars
      const { phrase, ...restQueries } = queries

      const checkPhraseArea = () => {
        if (phrase) {
          switch (phrase) {
            case 'شمال':
              return {
                province: ['گیلان', 'مازندران', 'گلستان'],
                meta_tags: ['north'],
              }
            case 'جنوب':
              return {
                city: ['چابهار', 'هرمز', 'قشم', 'کیش'],
                meta_tags: ['south'],
              }
            default:
              return { phrase }
          }
        }
      }

      const checkTehranNearCities = () => {
        const hasTehranNearCities = queries.near_cities?.find(
          item => item === 'تهران',
        )
        if (hasTehranNearCities) {
          return { meta_tags: ['around-tehran'] }
        }
      }
      const checkIsMap = () => {
        // if the request is from map
        if (Number(queries?.from_map_nearby)) {
          // if the page equals with undefined or page=1 or is x5 like page=(5, 10, 15, 20, ...) returns true
          if (!queries?.page || !(+queries?.page % 5) || +queries?.page === 1) {
            return true
          }
        }
      }

      const res: AxiosResponse<{
        data: {
          region_data: any
          records: HouseCardItemProps[]
          pagination: { total: number; current_page: number }
          min_price: any
          parent?: {
            title?: string
            city?: string
          }
          search_id?: string
        }
      }> =
        // eslint-disable-next-line new-cap
        await api.get('/house/search', {
          ...(checkIsMap()
            ? {
                ...restQueries,
                perpage: 64,
                page: Math.floor((Number(queries?.page) || 0) / 5) + 1, // while map includes 60~64 items, page 1~4 => 1 and page 5~9 => 2 and ...
              }
            : {
                ...restQueries,
                ...checkTehranNearCities(), //checkin phrase is near_cities=tehran
                ...checkPhraseArea(), // checkin phrase is shomal or jonoob
              }),
          'with[]': 'photos',
          ...(queries.mid_term ? { mid_term: 1, random: 1 } : {}),
        })

      dispatch({
        type: FETCH_SEARCH_ITEMS,
        list: Number(queries?.from_map_nearby)
          ? res?.data?.data?.records?.slice(0, Number(queries?.perpage))
          : res?.data?.data?.records,
        pagination: res?.data?.data?.pagination,
        region_data: res?.data.data?.region_data,
        loading: false,
        min_price: !res?.data?.data?.min_price?.amount
          ? {
              amount: 30000,
              currency_name: 'تومان',
              currency_short_form: 'ت',
            }
          : res?.data?.data?.min_price,
        parent: res?.data?.data?.parent,
        search_id: res?.data?.data?.search_id,
        map_list: res?.data?.data?.records,
        midterm_map_list: queries?.mid_term
          ? getState().search.midterm_map_list
          : null,
      })

      // Handle Res
    } catch (err) {
      // Handle Err

      if (err === 0) return // Rejected from request protector

      if ((err as AxiosError).response) {
        SentryClient.setExtra('Context', (err as AxiosError).response)
      }
      await SentryClient.captureException(`_error ${err}`)
      dispatch({
        type: FETCH_SEARCH_ITEMS,
        list: null,
        loading: false,
        pagination: null,
        region_data: null,
        min_price: {
          amount: 30000,
          currency_name: 'تومان',
          currency_short_form: 'ت',
        },
      })
    }
  }

export const fetchMidtermHouses: MainAction<any> =
  (queries: IHouseRequestType, callback: () => void) =>
  async (dispatch, getState, { Api, ctx }) => {
    const api = new Api({
      dispatch,
      getState,
      ctx,
    })

    try {
      const res: AxiosResponse<{
        data: {
          region_data: any
          records: HouseCardItemProps[]
          pagination: { total: number; current_page: number }
          min_price: any
          parent?: {
            title?: string
            city?: string
          }
          search_id?: string
        }
      }> =
        // eslint-disable-next-line new-cap
        await api.get('/house/search', {
          ...queries,
          perpage: 64,
          page: Math.floor((Number(queries?.page) || 0) / 5) + 1,
          'with[]': 'photos',
          mid_term: 1,
          random: 1,
        })

      dispatch({
        type: FETCH_SEARCH_ITEMS,
        ...getState().search,
        midterm_map_list: res.data.data.records,
      })
      callback && callback()
      // Handle Res
    } catch (err) {
      // Handle Err
      if (err === 0) return // Rejected from request protector
      dispatch({
        type: FETCH_SEARCH_ITEMS,
        ...getState().search,
      })
      callback && callback()
    }
  }

export const setFilters: MainAction<[MainFilters]> =
  (filters: MainFilters = {}) =>
  dispatch => {
    dispatch({
      type: SET_FILTERS,
      filters,
    })
  }

export const setBasicFilters: MainAction<[IBasicFilters?, IBasicFilters?]> =
  (data, dataToSetLs) => dispatch => {
    let mainData = data
    if (typeof window !== 'undefined') {
      if (data) {
        localStorage.setItem(
          'basicFilters',
          JSON.stringify(
            dataToSetLs
              ? { ...dataToSetLs, setTime: new Date().toUTCString() }
              : { ...data, setTimeFilter: new Date().toUTCString() },
          ),
        )
      } else {
        if (localStorage.getItem('basicFilters')) {
          mainData = JSON.parse(localStorage.getItem('basicFilters') || '')

          const laterDay = new Date().getTime() / 1000
          const earlierDay = new Date(mainData?.setTimeFilter).getTime() / 1000
          const diffMinutes = Math.round((laterDay - earlierDay) / 60) // Dividing by milliseconds per minute

          if (diffMinutes > 30) {
            localStorage.removeItem('basicFilters')
            mainData = undefined
          }
        }
      }
      if (mainData) {
        const { checkin, checkout, guests } = mainData

        dispatch({
          type: SET_BASIC_FILTERS,
          checkin,
          checkout,
          guests,
        })
      }
    }
  }

export const fetchPlaceFilter: MainAction =
  () =>
  async (dispatch, getState, { Api, ctx }) => {
    const api = new Api({ dispatch, getState, ctx })
    dispatch({
      type: FETCH_PLACE_ITEMS,
      data: null,
      loading: true,
    })
    try {
      const res: AxiosResponse = await api.get('/landing/tehran')
      dispatch({
        type: FETCH_PLACE_ITEMS,
        data: res.data.data,
        loading: false,
      })
    } catch (err) {
      dispatch({
        type: FETCH_PLACE_ITEMS,
        data: getState().placeFilter.data,
        loading: false,
      })
    }
  }

export const fetchFirstHouses: MainAction<any> =
  (queries: IHouseRequestType) =>
  async (dispatch, getState, { Api, ctx }) => {
    const api = new Api({ dispatch, getState, ctx })
    const {
      firstSearch: { list },
    } = getState()
    try {
      dispatch({
        type: FETCH_FIRST_SEARCH_ITEMS,
        ...getState().firstSearch,
        loading: true,
        list: [],
        map_list: [],
        ...(!getState().firstSearch?.min_price?.amount && {
          min_price: {
            amount: 30000,
            currency_name: 'تومان',
            currency_short_form: 'ت',
          },
        }),
        firstLoading: false,
      })

      // eslint-disable-next-line no-unused-vars
      const { phrase, ...restQueries } = queries

      const checkPhraseArea = () => {
        if (phrase) {
          switch (phrase) {
            case 'شمال':
              return { province: ['گیلان', 'مازندران', 'گلستان'] }
            case 'جنوب':
              return { city: ['چابهار', 'هرمز', 'قشم', 'کیش'] }
            default:
              return { phrase }
          }
        }
      }

      const checkIsMap = () => {
        // if the request is from map
        if (Number(queries?.from_map_nearby)) {
          // if the page equals with undefined or page=1 or is x5 like page=(5, 10, 15, 20, ...) returns true
          if (!queries?.page || !(+queries?.page % 5) || +queries?.page === 1) {
            return true
          }
        }
      }
      const request = performance.now()
      const res: AxiosResponse<{
        data: {
          region_data: any
          records: HouseCardItemProps[]
          pagination: { total: number; current_page: number }
          min_price: any
          parent?: {
            title?: string
            city?: string
          }
          search_id?: string
          firstLoading?: boolean
        }
      }> =
        // eslint-disable-next-line new-cap
        await api.get('/house/search', {
          ...(checkIsMap()
            ? {
                ...restQueries,
                perpage: 64,
                page: Math.floor((Number(queries?.page) || 0) / 5) + 1, // while map includes 60~64 items, page 1~4 => 1 and page 5~9 => 2 and ...
              }
            : {
                ...restQueries,
                ...checkPhraseArea(), // checkin phrase is shomal or jonoob
              }),
          'with[]': 'photos',
          // hide_test: 0,
        })
      const response = performance.now()
      dispatch({
        type: FETCH_FIRST_SEARCH_ITEMS,
        list: Number(queries?.from_map_nearby)
          ? res?.data?.data?.records.slice(0, Number(queries?.perpage))
          : res?.data?.data?.records,
        pagination: res?.data?.data?.pagination,
        region_data: res?.data.data?.region_data,
        loading: false,
        min_price: !res?.data?.data?.min_price?.amount
          ? {
              amount: 30000,
              currency_name: 'تومان',
              currency_short_form: 'ت',
            }
          : res?.data?.data?.min_price,
        parent: res?.data?.data?.parent,
        search_id: res?.data?.data?.search_id,
        map_list: res?.data?.data?.records,
        firstLoading: true,
      })
      const isdispatched = performance.now()

      heap.track('performance_search_fetchHouses', {
        responsTime: `${response - request}ms`,
        dispatchTime: `${isdispatched - response}ms`,
        total: `${isdispatched - request}ms`,
      })
      // Handle Res
    } catch (err) {
      // Handle Err

      if (err === 0) return // Rejected from request protector

      if ((err as AxiosError).response) {
        SentryClient.setExtra('Context', (err as AxiosError).response)
      }
      await SentryClient.captureException(`_error ${err}`)
      dispatch({
        type: FETCH_FIRST_SEARCH_ITEMS,
        list: null,
        loading: false,
        pagination: null,
        region_data: null,
        min_price: {
          amount: 30000,
          currency_name: 'تومان',
          currency_short_form: 'ت',
        },
        firstLoading: false,
      })
    }
  }

export const fetchNeighbourhoods: MainAction<[any?]> =
  data =>
  async (dispatch, getState, { Api, ctx }) => {
    const api = new Api({ dispatch, getState, ctx })
    dispatch({
      type: FETCH_NEIGHBOURHOODS,
      data: {
        list: getState().neighbourhoodsReducer.data.list,
        searchList: null,
      },
      loading: true,
    })
    try {
      let list = getState().neighbourhoodsReducer.data.list
      let searchList = []
      if (data) {
        const { data: searchData } = await api.get('/house/neighbourhoods', {
          phrase: data,
        })
        searchList = searchData.data
      } else {
        const { data: listData } = await api.get('/house/neighbourhoods')
        list = listData.data
      }
      dispatch({
        type: FETCH_NEIGHBOURHOODS,
        data: { list, searchList },
        loading: false,
      })
    } catch (err) {
      dispatch({
        type: FETCH_NEIGHBOURHOODS,
        data: {
          list: getState().neighbourhoodsReducer.data.list,
          searchList: getState().neighbourhoodsReducer.data.searchList,
        },
        loading: false,
      })
    }
  }
